import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { COMMON_URL, WEB_URL } from '../../utils/urls';
import { get } from '../../utils/data';
export default class LoginAPI {
  private request: TypeRequest;
  private host: string | undefined;
  private aid: number;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request, host, aid } = props;
    this.request = request;
    this.host = host;
    this.aid = aid;
  }

  /**
   * @param {string} mobile - the mobile used to login
   * @return {Promise} send result
   */
  sendCode = (params: {
    mobile: string;
    registerOnly?: boolean;
    extra_params?: object;
  }) => {
    const { mobile, registerOnly = false, extra_params = {} } = params;
    const data = encryptParams(
      { mobile, type: registerOnly ? 16 : 24, ...extra_params },
      ['mobile', 'type']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - the mobile used to login
   * @param {string} code - the captcha
   * @param {boolean} login_only - 'true' for only login, 'false' for register and login
   * @return {Promise} login result
   */
  smsLogin = (params: {
    mobile: string;
    code: string;
    login_only?: boolean;
    extra_params?: object;
  }) => {
    const { mobile, code, login_only = false, extra_params = {} } = params;
    const data = encryptParams({ mobile, code, ...extra_params }, [
      'mobile',
      'code',
    ]);
    const url = login_only ? WEB_URL.SMS_LOGIN_ONLY : WEB_URL.SMS_LOGIN;
    return new Promise((resolve, reject) => {
      this.request({
        url,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - the mobile used to login
   * @param {string} sms_code_key - the ticket gained from sms login only
   * @return {Promise} login result
   */
  smsLoginContinue = (params: {
    mobile: string;
    sms_code_key: string;
    extra_params?: object;
  }) => {
    const { mobile, sms_code_key, extra_params = {} } = params;
    const data = encryptParams({ mobile, sms_code_key, ...extra_params }, [
      'mobile',
      'sms_code_key',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SMS_LOGIN_CONTINUE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {number} platform_app_id - the id of third party
   * @param {string} next - redirect url after successful login
   * @return {Promise} login result
   */
  webLogin = (params: {
    platform_app_id: number;
    next: string;
    extra_params?: object;
  }) => {
    const { platform_app_id, next, extra_params = {} } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SMS_LOGIN_CONTINUE,
        method: 'POST',
        data: {
          platform_app_id,
          next,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email used to login
   * @param {string} password - corresponding password
   * @return {Promise} login result
   */
  emailLogin = (params: {
    email: string;
    password: string;
    extra_params?: object;
  }) => {
    const { email, password, extra_params = {} } = params;
    const data = encryptParams({ email, password, ...extra_params }, [
      'email',
      'password',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.EMAIL_LOGIN,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email to login
   * @param {string} code - the captcha
   * @param {string} service
   * @return {Promise} login result
   */
  emailCodeLogin = (params: {
    email: string;
    code: string;
    extra_params?: object;
  }) => {
    const { email, code, extra_params = {} } = params;
    const data = encryptParams({ email, code, ect_type: 13, ...extra_params }, [
      'email',
      'code',
    ]);
    return this.request({
      url: WEB_URL.EMAIL_CODE_LOGIN,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} account - the account to login
   * @param {string} password - corresponding password
   * @return {Promise} login result
   */
  pwdLogin = (params: {
    username?: string;
    mobile?: string;
    email?: string;
    account?: string;
    password: string;
    extra_params?: object;
  }) => {
    const {
      username,
      mobile,
      email,
      account,
      password,
      extra_params = {},
    } = params;
    const data = encryptParams(
      { username, mobile, email, account, password, ...extra_params },
      ['username', 'mobile', 'email', 'account', 'password']
    );
    return this.request({
      url: WEB_URL.PWD_LOGIN,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {number} platform_app_id - the app id of the third party used to login
   * @param {string} code - authorization code
   * @param {string} access_token - third-party authorization token
   * @param {string} access_token_secret - id token, only neccessary when using google to login
   * @param {string} openid - third-party openid, used with access_token
   * @param {string} profile_key - third-party ticket that preserves information temporaily, used when the login progress is blocked
   * @param {boolean} login_only - whether to login only, not allowing to register
   * @return {Promise} login result
   */
  authLogin = (params: {
    platform_app_id: number;
    code?: string;
    access_token?: string;
    access_token_secret?: string;
    openid?: string;
    profile_key?: string;
    login_only?: boolean;
    extra_params?: object;
  }) => {
    const {
      platform_app_id,
      code,
      access_token,
      access_token_secret,
      openid,
      profile_key,
      login_only = false,
      extra_params = {},
    } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: login_only ? WEB_URL.AUTH_LOGIN_ONLY : WEB_URL.AUTH_LOGIN,
        method: 'POST',
        data: {
          platform_app_id,
          code,
          access_token,
          access_token_secret,
          openid,
          profile_key,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  webShareLogin = (params: {
    access_token?: string;
    access_token_secret?: string;
    code?: string;
    expires_in?: string;
    openid?: string;
    profile_key?: string;
    platform_app_id: number;
    platform: string;
    mid?: string;
    verify_type?: string;
    verified_ticket?: string;
    extra_params?: object;
  }) => {
    const { extra_params = {}, ...props } = params;
    return this.request({
      url: WEB_URL.WEB_SHARE_LOGIN,
      method: 'POST',
      data: {
        ...extra_params,
        ...props,
      },
    });
  };

  /**
   * @param {string} access_token - third-party authorization token
   * @param {string} access_token_secret - id token, only neccessary when using google to login
   * @param {string} code - authorization code
   * @param {number} expires_in - expires time of access_token
   * @param {string} openid - third-party openid, used with access_token
   * @param {number} platform_app_id - the app id of the third party used to login
   * @param {string} platform - name of third party platform
   * @param {boolean} need_plaintext - whether to return mobile in plaintext
   * @param {string} profile_key - third-party ticket that preserves information temporaily
   * @return {Promise} login result
   */
  getOauthMobile = (params: {
    access_token?: string;
    access_token_secret?: string;
    code?: string;
    expires_in?: number;
    openid?: string;
    platform_app_id: number;
    platform: string;
    need_plaintext?: boolean;
    profile_key?: string;
    extra_params?: object;
  }) => {
    const {
      access_token,
      access_token_secret,
      code,
      expires_in,
      openid,
      platform_app_id,
      platform,
      need_plaintext = false,
      profile_key,
      extra_params = {},
    } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.MOBILE_OAUTH,
        method: 'POST',
        data: {
          access_token,
          access_token_secret,
          code,
          expires_in,
          openid,
          platform_app_id,
          platform,
          need_plaintext,
          profile_key,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {number} platform_app_id - the app id of the third party used to login
   * @param {string} code - authorization code
   * @param {string} profile_key - third-party ticket that preserves information temporaily, used when the login progress is blocked
   * @param {string} mobile - phone number
   * @param {string} sms_code - captcha
   * @return {Promise} login result
   */
  authBindLogin = (params: {
    platform_app_id: number;
    code?: string;
    profile_key?: string;
    mobile?: string;
    sms_code?: string;
    sms_code_key?: string;
    extra_params?: object;
  }) => {
    const {
      platform_app_id,
      code,
      mobile,
      sms_code,
      sms_code_key,
      profile_key,
      extra_params = {},
    } = params;
    const data = encryptParams(
      {
        platform_app_id,
        code,
        mobile,
        sms_code,
        sms_code_key,
        profile_key,
        ...extra_params,
      },
      ['mobile', 'sms_code']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.AUTH_BIND_ONLY,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - the mobile used to login
   * @param {string} code - the captcha
   * @param {string} profile_key - a parameter returned by login_only interface
   */
  mobileBindLogin = (params: {
    mobile: string;
    code: string;
    profile_key: string;
    extra_params?: object;
  }) => {
    const { code, mobile, profile_key, extra_params = {} } = params;
    const data = encryptParams(
      {
        code,
        mobile,
        profile_key,
        ...extra_params,
      },
      ['mobile', 'code']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.MOBILE_BIND_ONLY,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} next - the redirect url after successful logout
   */
  logout = (params: {
    next: string;
    need_redirect?: number;
    extra_params?: object;
  }) => {
    const { next = '/', need_redirect = 1 } = params;
    if (need_redirect) {
      const aid = get('initProps')?.aid || '';
      const nextUrl = encodeURIComponent(next);
      window.location.href = `${WEB_URL.LOGOUT}?account_sdk_source=web&aid=${aid}&next=${nextUrl}`;
      return;
    } else {
      return this.request({
        url: WEB_URL.LOGOUT,
        data: {
          need_redirect,
        },
      });
    }
  };

  /**
   * @param {string} email - the email used to login
   * @param {string} next - the url attached to the email
   * @param {string} password - corresponding password
   * @return {Promise} send result
   */
  sendEmailCode = (params: {
    email: string;
    next?: string;
    password?: string;
    extra_params?: object;
  }) => {
    const { email, next, password, extra_params = {} } = params;
    const data = encryptParams(
      {
        email,
        next,
        password,
        type: 1,
        ...extra_params,
      },
      ['email', 'password', 'type']
    );
    return this.request({
      url: WEB_URL.SEND_EMAIL_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to login
   * @param {string} next - the url attached to the email
   * @return {Promise} send result
   */
  sendEmailLoginCode = (params: {
    email: string;
    next?: string;
    extra_params?: object;
  }) => {
    const { email, next, extra_params = {} } = params;
    const data = encryptParams(
      {
        email,
        next,
        type: 13,
        ...extra_params,
      },
      ['email', 'type']
    );
    return this.request({
      url: WEB_URL.SEND_EMAIL_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to login
   * @param {string} code - the captcha
   * @param {string} password - corresponding password
   * @return {Promise} send result
   */
  emailRegister = (params: {
    email: string;
    code: string;
    password?: string;
    ifLogin?: boolean;
    extra_params?: object;
  }) => {
    const {
      email,
      code,
      password,
      ifLogin = false,
      extra_params = {},
    } = params;
    const data = encryptParams(
      {
        email,
        code,
        password,
        type: 1,
        ...extra_params,
      },
      ['email', 'password', 'type', 'code']
    );
    return this.request({
      url: ifLogin ? WEB_URL.EMAIL_REGISTER_LOGIN : WEB_URL.EMAIL_REGISTER,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} mobile - the mobile used to login
   * @param {string} code - the captcha
   * @param {string} password - corresponding password
   * @return {Promise} register result
   */
  mobileRegister = (params: {
    mobile: string;
    code: string;
    password: string;
    extra_params?: object;
  }) => {
    const { mobile, code, password, extra_params = {} } = params;
    const data = encryptParams(
      {
        mobile,
        code,
        password,
        type: 16,
        ...extra_params,
      },
      ['mobile', 'code', 'password', 'type']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.MOBILE_REGISTER,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - the mobile used to login
   * @param {string} sms_code - the sms captcha
   * @param {string} email - the email used to login
   * @param {string} email_code - the email captcha
   * @param {string} password - corresponding password
   * @param {string} verify_ticket - the ticket returned by server during last verification process
   * @return {Promise} register result
   */
  mobileEmailRegister = (params: {
    mobile: string;
    sms_code: string;
    email: string;
    email_code: string;
    password: string;
    verify_ticket?: string;
    extra_params?: object;
  }) => {
    const {
      mobile,
      sms_code,
      email,
      email_code,
      password,
      verify_ticket,
      extra_params = {},
    } = params;
    const data = encryptParams(
      {
        mobile,
        sms_code,
        email,
        email_code,
        password,
        verify_ticket,
        sms_type: 16,
        ect_type: 1,
        ...extra_params,
      },
      [
        'mobile',
        'sms_code',
        'sms_type',
        'email',
        'email_code',
        'ect_type',
        'password',
      ]
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.MOBILE_EMAIL_REGISTER,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @return {Promise} check result
   */
  checkLogin = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    return this.request({
      url: WEB_URL.CHECK_LOGIN,
      method: 'GET',
      data: extra_params,
    });
  };

  /**
   * @param {string} code - user code returned by open service
   * @param {string} profile_key - user code returned by other process
   * @param {number} platform_app_id - apply for configuration in appcloud
   * @param {string} platform - third-party name
   * @param {number} need_mobile - whether synchronize the mobile of third-party account
   * @param {number} check_mobile - whether restrict old user to bind mobile forcely
   * @param {number} change_bind - whether rebind forcely when conflict occurs
   * @return {Promise} login result
   */
  bindWithMobileLogin = (params: {
    code?: string;
    profile_key?: string;
    platform_app_id: number;
    platform: string;
    need_mobile?: number;
    check_mobile?: number;
    change_bind?: number;
    extra_params?: object;
  }) => {
    const { extra_params = {}, ...props } = params;
    return this.request({
      url: WEB_URL.BIND_WITH_MOBILE_LOGIN,
      method: 'POST',
      data: { ...extra_params, ...props },
    });
  };

  /**
   * @param {string} access_token
   * @param {string} access_token_secret
   * @param {string} code - user code returned by open service
   * @param {number} openid
   * @param {string} profile_key - user code returned by other process
   * @param {number} platform_app_id - apply for configuration in appcloud
   * @param {string} redirect_uri
   * @return {Promise} login result
   */
  bindWithEmailLogin = (params: {
    access_token?: string;
    access_token_secret?: string;
    code?: string;
    openid?: number;
    profile_key?: string;
    platform_app_id: number;
    redirect_uri?: string;
    extra_params?: object;
  }) => {
    const { extra_params = {}, ...props } = params;
    return this.request({
      url: WEB_URL.BIND_WITH_EMAIL_LOGIN,
      method: 'POST',
      data: { ...extra_params, ...props },
    });
  };

  /**
   * @param {number} platform_app_id - apply for configuration in appcloud
   * @param {string} next - redirect url after successful login
   * @param {number} use_local_host
   * @param {string} action - login_only || ''
   */
  thirdAuth = (params: {
    platform_app_id: number;
    next: string;
    use_local_host?: number;
    action?: string;
  }) => {
    const { platform_app_id, next = '/', use_local_host = 0, action } = params;
    const aid = get('initProps')?.aid || '';
    const nextUrl = encodeURIComponent(next);
    window.location.href = `${WEB_URL.THIRD_AUTH}?account_sdk_source=web&aid=${aid}&next=${nextUrl}&platform_app_id=${platform_app_id}&use_local_host=${use_local_host}&action=${action}`;
  };

  /**
   * @param {string} username
   * @param {string} password
   * @return {Promise} register result
   */
  usernameRegister = (params: {
    username: string;
    password: string;
    extra_params?: object;
  }) => {
    const { username, password, extra_params = {} } = params;
    let data = encryptParams({ username, password, ...extra_params }, [
      'username',
      'password',
    ]);
    return this.request({
      url: WEB_URL.USERNAME_REGISTER,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - user email
   * @param {string} password - password
   * @param {number} next_url - redirect url
   * @param {string} recaptcha_token - token
   * @return {Promise} login result
   */
  emailRegisterNoVerifyV2 = (params: {
    email: string;
    password: string;
    next_url?: string;
    recaptcha_token?: string;
    extra_params?: object;
  }) => {
    const {
      email,
      password,
      next_url,
      recaptcha_token,
      extra_params = {},
    } = params;
    const data = encryptParams(
      {
        email,
        password,
        next_url,
        recaptcha_token,
        ...extra_params,
      },
      ['email', 'password', 'recaptcha_token']
    );
    return this.request({
      url: WEB_URL.EMAIL_REGISTER_NO_VERIFY_V2,
      method: 'POST',
      data,
    });
  };
  /**
   * [wap login]
   * @param {string} origin request domain, this interface isn't sso
   * @param {string} platform_app_id the thrid appId
   * @param {string} next rederict url
   * @param {string} type default 'sso'
   * @param {string} use_local_host default 1
   * @param {string} scopes default 'user_info'
   * @param {string} url_params
   */
  wapLogin = (params: {
    origin?: string;
    platform_app_id?: string; // platform_app_id or platform
    platform?: string;
    next: string;
    type?: string;
    use_local_host?: number;
    scopes?: string;
    url_params?: string;
    replace?: boolean;
  }): void => {
    const {
      origin = '',
      type = 'web',
      use_local_host,
      scopes = 'user_info',
      next,
      url_params = '',
      platform_app_id,
      platform = '',
      replace = false,
    } = params;
    const service = encodeURIComponent(next);
    const realNext = encodeURIComponent(
      `${this.host}/auth/login_success/?service=${service}`
    );
    const paramMap: any = {
      aid: this.aid,
      type,
      use_local_host,
      scopes,
      next: realNext,
      url_params,
      platform_app_id,
      platform,
    };
    let query = '';
    const len = Object.keys(paramMap).length;
    Object.keys(paramMap).forEach((key, index) => {
      if (len === index + 1) {
        query += `${key}=${paramMap[key]}`;
      } else {
        query += `${key}=${paramMap[key]}&`;
      }
    });
    const redirectUrl = `${origin}${WEB_URL.AUTH_LOGIN_URL}?${query}`;
    // open url in current window
    if (replace) {
      window.location.href = redirectUrl;
    } else {
      // TODO
      const childWindow = window.open(redirectUrl, '_blank');
    }
  };

  /**
   * [register - bind verify info]
   * @param {string} auth_ticket ticket from certification platform
   * @param {string} verify_ticket ticket from passport
   * @param {string} token token that identify user
   * @param {string} scene scene like 'shop_guide_bind'
   * @param {string} user_type such as, 0 for normal user, 1 for lark user, 2 for xxx employee, 3 for xxx shop guide
   */
  bindVerifyInfo = (params: {
    auth_ticket: string;
    verify_ticket: string;
    token?: string;
    scene: string;
    user_type: 0 | 1 | 2 | 3;
    extra_params?: object;
  }) => {
    const {
      auth_ticket,
      verify_ticket,
      token,
      scene,
      user_type,
      extra_params = {},
    } = params;
    return this.request({
      url: COMMON_URL.BIND_VERIFY_INFO,
      method: 'GET',
      data: {
        auth_ticket,
        verify_ticket,
        token,
        scene,
        user_type,
        ...extra_params,
      },
    });
  };
}
